import React from "react";
import { Flame, Play } from "lucide-react";

/**
 * FeedTrendingRow — "Trending now · last 6h" horizontal rail. Self-hides
 * when there's no data (parent passes an empty array on quiet days).
 *
 * Props:
 *   videos        — array of trending video objects
 *   onSelectVideo — (video) => void, fires when user taps a trending tile.
 *                   Parent typically does: handleClosePlayer() →
 *                   navigate(`/${v.username}?video=${v.id}`).
 */
export const FeedTrendingRow = ({ videos, onSelectVideo }) => {
  if (!videos || videos.length === 0) return null;
  return (
    <div className="mt-10 max-w-5xl mx-auto text-left" data-testid="trending-row">
      <div className="flex items-end justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-orange-400" />
          <h2 className="text-white font-semibold text-base sm:text-lg">Trending now</h2>
          <span className="text-gray-500 text-xs">· last 6h</span>
        </div>
        <span className="text-gray-500 text-[11px] hidden sm:inline">most-looped videos right now</span>
      </div>
      <div className="flex sm:grid sm:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto sm:overflow-visible pb-2 px-1 -mx-1 snap-x snap-mandatory sm:snap-none">
        {videos.map((v, idx) => (
          <button
            key={v.id}
            onClick={() => onSelectVideo(v)}
            className="group shrink-0 sm:shrink w-44 sm:w-auto snap-start text-left bg-gray-900/70 hover:bg-gray-800 border border-gray-800 hover:border-orange-500/40 rounded-lg overflow-hidden transition-all"
            data-testid={`trending-video-${v.id}`}
          >
            <div className="relative aspect-video bg-gradient-to-br from-orange-950/40 via-gray-900 to-gray-950">
              {v.thumbnail_url ? (
                <img
                  src={v.thumbnail_url}
                  alt={v.title}
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Play className="h-7 w-7 text-orange-400/70" fill="currentColor" />
                </div>
              )}
              <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-orange-500/90 text-black font-bold rounded px-1.5 py-0.5">
                <Flame className="h-2.5 w-2.5" /> {v.recent_loops}
              </span>
              <span className="absolute bottom-1.5 right-1.5 text-[10px] bg-black/70 text-gray-300 rounded px-1.5 py-0.5">#{idx + 1}</span>
            </div>
            <div className="p-2.5">
              <p className="text-white text-xs sm:text-sm font-medium truncate group-hover:text-orange-300 transition-colors">
                {v.title || "(untitled)"}
              </p>
              <p className="text-gray-500 text-[10px] mt-0.5 truncate">
                /{v.username} · {v.location?.city || v.location?.country || "somewhere"}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
